import { motion } from "framer-motion";
import {
  Music2,
  Globe2,
  Headphones,
  Sparkles,
  ArrowUpRight,
} from "lucide-react";

import { ARTIST } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

// SoundCloud embed URL — visual player skinned with brand gold.
// The `on.soundcloud.com/…` short URL is a redirect wrapper the widget cannot
// resolve, so we point the widget to the resolved profile URL directly.
const SC_TARGET = "https://soundcloud.com/irwancrawofficial";
const SC_EMBED = `https://w.soundcloud.com/player/?url=${encodeURIComponent(
  SC_TARGET
)}&color=%23c9a961&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;

const FEATURES = [
  {
    icon: Music2,
    title: "Curated DJ Sets",
    text: "Carefully crafted musical journeys.",
  },
  {
    icon: Globe2,
    title: "Global Influences",
    text: "Afro House, Organic House, Deep House and Melodic House.",
  },
  {
    icon: Headphones,
    title: "Club Experience",
    text: "Music designed for clubs, festivals and luxury venues.",
  },
  {
    icon: Sparkles,
    title: "Immersive Atmospheres",
    text: "Organic percussion, deep grooves and cinematic melodies.",
  },
];

const PARTICLES = [
  { x: 4, y: 30, s: 1.4, d: 0.4 },
  { x: 22, y: 78, s: 1.8, d: 2.3 },
  { x: 41, y: 20, s: 1.4, d: 1.1 },
  { x: 60, y: 66, s: 2, d: 3.5 },
  { x: 78, y: 30, s: 1.6, d: 4.6 },
  { x: 92, y: 84, s: 2, d: 1.9 },
];

export const DJMixes = () => {
  return (
    <section
      id="dj-mixes"
      data-testid="dj-mixes-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      {/* Ambient gold gradients */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 55% 40% at 85% 15%, rgba(201,169,97,0.10), transparent 60%), radial-gradient(ellipse 55% 40% at 10% 90%, rgba(201,169,97,0.08), transparent 60%)",
        }}
      />

      {/* Floating particles */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ opacity: 0, y: 0 }}
            animate={{
              opacity: [0, 0.7, 0.35, 0.7, 0],
              y: [0, -30, -60, -100, -140],
            }}
            transition={{
              duration: 15 + (i % 4) * 2,
              delay: p.d,
              ease: "linear",
              repeat: Infinity,
            }}
            className="absolute rounded-full"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: `${p.s}px`,
              height: `${p.s}px`,
              background:
                "radial-gradient(circle, rgba(229,204,148,0.9) 0%, rgba(201,169,97,0.4) 60%, rgba(201,169,97,0) 100%)",
              boxShadow: "0 0 6px rgba(229,204,148,0.6)",
            }}
          />
        ))}
      </div>

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        {/* Header */}
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold">
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 04 — Sets
            </p>
            <h2
              data-testid="dj-mixes-title"
              className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              DJ <span className="font-editorial text-gold-hi">Mixes</span>
            </h2>
          </motion.div>
          <motion.p
            variants={fadeUp}
            className="max-w-md text-sm text-muted leading-relaxed"
          >
            Discover immersive Afro House, Organic House, Deep House and
            Melodic House journeys through exclusive DJ sets curated by Irwan
            Craw.
          </motion.p>
        </motion.div>

        {/* Intro */}
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-3xl font-editorial text-xl md:text-2xl text-ink/90 leading-snug mb-10 md:mb-12"
          data-testid="dj-mixes-intro"
        >
          More than official releases, these DJ sets represent Irwan Craw's
          musical identity through carefully crafted journeys blending{" "}
          <span className="text-gold-hi">Afro House</span>,{" "}
          <span className="text-gold-hi">Organic House</span>,{" "}
          <span className="text-gold-hi">Deep House</span>,{" "}
          <span className="text-gold-hi">Melodic House</span> and Latin
          influences.
        </motion.p>

        {/* Player glass card */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="relative overflow-hidden rounded-sm border border-[color:var(--gold)]/30 bg-gradient-to-br from-[color:var(--gold)]/[0.05] via-transparent to-[color:var(--gold)]/[0.03] p-4 md:p-6 backdrop-blur-md shadow-[0_0_40px_rgba(0,0,0,0.4)]"
        >
          {/* Corner ornaments */}
          <span className="pointer-events-none absolute right-4 top-3 font-editorial text-lg text-[color:var(--gold-hi)]/60">
            ✦
          </span>
          <span className="pointer-events-none absolute left-4 bottom-3 text-[9px] font-bold uppercase tracking-[0.28em] text-gold">
            Now Streaming · SoundCloud
          </span>

          <div className="relative rounded-sm overflow-hidden border border-[color:var(--line-strong)] bg-black">
            <iframe
              data-testid="dj-mixes-soundcloud-iframe"
              title="Irwan Craw — DJ Mixes"
              width="100%"
              height="480"
              scrolling="no"
              frameBorder="0"
              allow="autoplay"
              src={SC_EMBED}
              className="block"
              loading="lazy"
            />
          </div>
        </motion.div>

        {/* Feature cards */}
        <motion.div
          variants={stagger(0.1, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
        >
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                variants={fadeUp}
                data-testid={`dj-mixes-feature-${i}`}
                className="group relative overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2/60 backdrop-blur-md p-6 transition-all duration-700 hover:border-[color:var(--gold)]/50 hover:-translate-y-0.5"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--gold)]/40 text-gold transition-all duration-500 group-hover:border-[color:var(--gold)] group-hover:bg-[color:var(--gold)] group-hover:text-[#0a0a0a]">
                  <Icon size={14} />
                </span>
                <h3 className="mt-5 font-display text-base md:text-lg font-bold uppercase tracking-[0.06em] text-ink">
                  {f.title}
                </h3>
                <p className="mt-2 text-sm text-muted leading-relaxed">
                  {f.text}
                </p>

                {/* Hover glow */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 15%, rgba(201,169,97,0.12), transparent 55%)",
                  }}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          variants={stagger(0.1, 0.1)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mt-14 md:mt-16 flex flex-col items-center text-center"
        >
          <motion.p
            variants={fadeUp}
            className="font-editorial text-2xl md:text-3xl text-ink/90"
          >
            Enjoy the journey?
          </motion.p>
          <motion.p
            variants={fadeUp}
            className="mt-2 max-w-lg text-sm text-muted leading-relaxed"
          >
            Follow Irwan Craw on SoundCloud for future DJ sets and exclusive
            mixes.
          </motion.p>
          <motion.a
            variants={fadeUp}
            data-testid="dj-mixes-follow-cta"
            href={ARTIST.soundcloudUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="group relative mt-8 inline-flex items-center gap-3 overflow-hidden rounded-full bg-[color:var(--gold)] px-7 py-4 text-[11px] font-bold uppercase tracking-[0.28em] text-[#0a0a0a] transition-all duration-500 hover:shadow-[0_0_45px_rgba(201,169,97,0.55)]"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/60 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full"
            />
            <span className="relative">Follow on SoundCloud</span>
            <ArrowUpRight
              size={14}
              className="relative transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};
